import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  BASE,
  FEEDBACK_KINDS,
  FEEDBACK_STATUS,
  type FeedbackAdmin as Item,
  type FeedbackStatus,
} from '../lib/api';
import { dNum, hm } from '../lib/domain';
import { useMe, useToast } from '../hooks';
import { ErrorBox, Icon, Loading, PageHead } from '../ui';
import { AdminTabs } from './Admin';

type Filter = FeedbackStatus | 'all';

/** Admins: all feedback, change status/answer, export open items (the answers ship with the next release). */
export default function FeedbackAdmin() {
  const { data: me } = useMe();
  const [filter, setFilter] = useState<Filter>('open');
  const q = useQuery({
    queryKey: ['feedback', 'admin', filter],
    queryFn: () =>
      api<{ items: Item[]; counts: Partial<Record<FeedbackStatus, number>> }>(
        `/feedback${filter === 'all' ? '' : `?status=${filter}`}`,
      ),
    enabled: me?.role === 'admin',
  });
  if (me && me.role !== 'admin')
    return (
      <>
        <PageHead title="Nur für Admins" />
        <p>
          Dein eigenes Feedback findest du im <Link to="/profil#feedback">Profil</Link>.
        </p>
      </>
    );

  const counts = q.data?.counts ?? {};
  return (
    <>
      <PageHead
        title="Feedback"
        right={
          <div className="fb-actions">
            <a className="btn btn-ghost" href={`${BASE}/api/feedback/export`} download>
              <Icon name="down" sw={1.8} />
              Exportieren
            </a>
          </div>
        }
      />
      <AdminTabs />
      <p className="note">
        „Exportieren“ lädt alle offenen und geplanten Einträge als Markdown-Datei herunter – ohne Namen und
        E-Mail-Adressen. Die Antworten kommen mit der nächsten Version und werden beim Start automatisch übernommen.
      </p>
      <div className="toolbar">
        <div className="seg" role="group" aria-label="Status">
          {(['open', 'planned', 'done', 'wontfix', 'all'] as Filter[]).map(s => (
            <button key={s} aria-pressed={filter === s} onClick={() => setFilter(s)}>
              {s === 'all' ? 'Alle' : FEEDBACK_STATUS[s]}
              {s !== 'all' && counts[s] ? ` (${counts[s]})` : ''}
            </button>
          ))}
        </div>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorBox error={q.error} />
      ) : !q.data?.items.length ? (
        <div className="empty">
          <b>Keine Einträge.</b>
        </div>
      ) : (
        <div className="fb-list">
          {q.data.items.map(f => (
            <FeedbackItem key={`${f.id}-${f.updated_at}`} f={f} />
          ))}
        </div>
      )}
    </>
  );
}

function FeedbackItem({ f }: { f: Item }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<FeedbackStatus>(f.status);
  const [note, setNote] = useState(f.note);
  const [version, setVersion] = useState(f.resolved_version ?? '');
  const save = useMutation({
    mutationFn: () =>
      api(`/feedback/${f.id}`, {
        method: 'PATCH',
        body: { status, note, ...(version.trim() ? { version: version.trim() } : {}) },
      }),
    onSuccess: () => {
      toast(`#${f.id} gespeichert`);
      void qc.invalidateQueries({ queryKey: ['feedback'] });
    },
    onError: e => toast(e.message),
  });
  const created = new Date(f.created_at);
  const internal = f.page.startsWith('/') && !f.page.startsWith('//');
  return (
    <article className="panel fb-item">
      <div className="fb-meta">
        <b>#{f.id}</b>
        <span className={`chg chg-${f.kind}`}>{FEEDBACK_KINDS[f.kind]}</span>
        <span>
          {f.author ?? 'gelöschtes Konto'} · {dNum(created)} {hm(created)} · Version {f.app_version}
          {f.context.device ? ` · ${f.context.device}` : ''}
          {f.context.viewport ? ` · ${f.context.viewport}` : ''}
        </span>
        {f.page && (internal ? <Link to={f.page}>{f.page}</Link> : <code>{f.page}</code>)}
      </div>
      <p className="fb-body">{f.body}</p>
      <div className="fb-edit">
        <label className="fl">
          <span>Status</span>
          <select value={status} onChange={e => setStatus(e.target.value as FeedbackStatus)}>
            {(Object.keys(FEEDBACK_STATUS) as FeedbackStatus[]).map(s => (
              <option key={s} value={s}>
                {FEEDBACK_STATUS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="fl">
          <span>Version</span>
          <input
            value={version}
            onChange={e => setVersion(e.target.value)}
            placeholder={status === 'done' ? 'aktuelle' : '–'}
            inputMode="decimal"
            maxLength={20}
          />
        </label>
        <label className="fl fb-note">
          <span>Antwort an das Mitglied</span>
          <input value={note} onChange={e => setNote(e.target.value)} maxLength={1000} />
        </label>
        <button className="btn btn-navy btn-sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </button>
      </div>
    </article>
  );
}
